import { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { Shield, X, Settings2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Link, useLocation } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'zouqai_cookie_consent';
const HIDDEN_PREFIXES = ['/dashboard', '/pos', '/kitchen', '/driver', '/onboard', '/admin'];

export default function CookieConsentBanner() {
  const { isAr } = useLanguage();
  const { pathname } = useLocation();
  const l = (ar, en) => isAr ? ar : en;
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: true, marketing: false });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
    setVisible(false);
  };

  const acceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const rejectOptional = () => save({ essential: true, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  if (!visible || HIDDEN_PREFIXES.some(p => pathname.startsWith(p))) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-consent-banner">
      <div className="max-w-2xl mx-auto rounded-2xl border border-border bg-card shadow-2xl shadow-black/20 overflow-hidden" style={{ backdropFilter: 'blur(16px)' }}>
        {/* Main Banner */}
        <div className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-4.5 h-4.5 text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-foreground mb-1" data-testid="cookie-title">
                {l('حماية خصوصيتك تهمنا', 'Your Privacy Matters')}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {l(
                  'نستخدم ملفات تعريف الارتباط لتحسين تجربتك. يمكنك قبول الكل أو تخصيص اختياراتك. اقرأ ',
                  'We use cookies to improve your experience. You can accept all or customize your choices. Read our '
                )}
                <Link to="/privacy" className="text-amber-500 hover:underline" data-testid="cookie-privacy-link">
                  {l('سياسة الخصوصية', 'Privacy Policy')}
                </Link>
                {l(' لمزيد من التفاصيل.', ' for more details.')}
              </p>
            </div>
            <button onClick={rejectOptional} className="text-muted-foreground hover:text-foreground p-1" data-testid="cookie-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Detail Panel */}
          {showDetails && (
            <div className="mb-4 p-4 bg-muted/50 rounded-xl space-y-3" data-testid="cookie-details">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-foreground">{l('ملفات أساسية', 'Essential Cookies')}</p>
                  <p className="text-[10px] text-muted-foreground">{l('ضرورية لعمل الموقع', 'Required for site functionality')}</p>
                </div>
                <Switch checked disabled data-testid="cookie-essential-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-foreground">{l('ملفات التحليلات', 'Analytics Cookies')}</p>
                  <p className="text-[10px] text-muted-foreground">{l('تساعدنا على فهم استخدام الموقع', 'Help us understand site usage')}</p>
                </div>
                <Switch checked={prefs.analytics} onCheckedChange={v => setPrefs(p => ({ ...p, analytics: v }))} data-testid="cookie-analytics-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-foreground">{l('ملفات تسويقية', 'Marketing Cookies')}</p>
                  <p className="text-[10px] text-muted-foreground">{l('لعرض محتوى مخصص', 'For personalized content')}</p>
                </div>
                <Switch checked={prefs.marketing} onCheckedChange={v => setPrefs(p => ({ ...p, marketing: v }))} data-testid="cookie-marketing-switch" />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button onClick={acceptAll} className="flex-1 h-9 rounded-xl text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="cookie-accept-all">
              <Check className="w-3.5 h-3.5 mr-1.5" />
              {l('قبول الكل', 'Accept All')}
            </Button>
            {showDetails ? (
              <Button onClick={saveCustom} variant="outline" className="flex-1 h-9 rounded-xl text-xs" data-testid="cookie-save-custom">
                {l('حفظ التفضيلات', 'Save Preferences')}
              </Button>
            ) : (
              <Button onClick={() => setShowDetails(true)} variant="outline" className="flex-1 h-9 rounded-xl text-xs" data-testid="cookie-customize">
                <Settings2 className="w-3.5 h-3.5 mr-1.5" />
                {l('تخصيص', 'Customize')}
              </Button>
            )}
            <Button onClick={rejectOptional} variant="ghost" className="h-9 rounded-xl text-xs text-muted-foreground" data-testid="cookie-reject">
              {l('رفض الاختياري', 'Reject Optional')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
